// PSLE study tip articles — loaded by Articles section and ArticlePage route.
// Articles are published monthly; newest first.

const RAW_ARTICLES = [
  // --- 2025-12 : NEW ---
  {
    slug: "parents-study-habits-at-home",
    subject: "Parents",
    publishedAt: "2025-12-05",
    title: "A Parent's Guide to Study Habits at Home",
    summary:
      "What actually moves a child's grade between classes — routines, desk setup, and how to talk about school without starting a fight.",
    readTime: "6 min read",
    hero: "/illustrations/signup-student.png",
    intro:
      "Tuition helps, but 80% of progress happens at home — in the 15-minute study blocks, the way you ask about school, the phone rules, and the desk setup. Here are the habits that quietly move grades upward, and the ones that quietly hurt.",
    dos: [
      "DO set a fixed 'homework start time' every weekday. Routine beats motivation — children stop negotiating once the clock decides.",
      "DO create a tidy, distraction-free desk with good light, water nearby, and the phone in another room. Environment cues concentration.",
      "DO ask specific questions after school: 'What's one thing you learnt today?' instead of 'How was school?'",
      "DO praise effort and process ('I saw you tried a model for that sum'), not just results ('Good, you got 95').",
      "DO review the weekly notes with your child on Sundays — 10 minutes, casual, just looking together.",
      "DO make sleep non-negotiable — 9–10 hours for primary-school kids. Tired brains make careless mistakes.",
    ],
    donts: [
      "DON'T hover while your child does homework. It breeds dependence and anxiety.",
      "DON'T compare siblings or cousins. Every child's pace is different and comparison erodes confidence fastest.",
      "DON'T pile on extra assessment books if the school workload is already heavy. Quality > quantity.",
      "DON'T use phones/tablets as a 'finish homework first' reward. It turns study into punishment.",
      "DON'T react emotionally to a poor test. Wait a day, then look at the paper together calmly.",
      "DON'T talk about PSLE as a life-or-death exam. Pressure narrows thinking; it doesn't sharpen it.",
    ],
    closing:
      "Steady parents raise steady students. A warm, predictable home does more for grades than any expensive assessment book.",
  },

  // --- 2025-11 ---
  {
    slug: "psle-math-ratio-percentage-rate-dos-donts",
    subject: "Math",
    publishedAt: "2025-11-08",
    title: "PSLE Math Ratio, Percentage & Rate: Do's & Don'ts",
    summary:
      "The three siblings of the PSLE syllabus share the same secret — they're all really about comparing parts. Here's how to see them clearly.",
    readTime: "5 min read",
    hero: "/illustrations/hero-class.png",
    intro:
      "Ratio, percentage, and rate show up in almost every PSLE Math paper — usually dressed up in a story. Students who recognise them quickly save 5–7 minutes per paper. The trick is to treat all three as 'comparing parts' problems.",
    dos: [
      "DO convert every ratio into units the moment you read the question. '3 : 5' = 8 units, done.",
      "DO use a model for 'before and after' ratio questions — the bar diagram exposes which quantity stays constant.",
      "DO convert percentage to fraction when the numbers are messy. 12.5% = 1/8, 87.5% = 7/8 — memorise these.",
      "DO draw a table for rate problems: rate × time = work. Fill in what's given, solve for the missing slot.",
      "DO double-check whether a percentage is of the original or the new quantity ('decrease by 20%' vs '20% of'). Most careless losses hide here.",
      "DO write the full answer statement with the correct unit: 'Ans: 60 apples' not just '60'.",
    ],
    donts: [
      "DON'T add or subtract ratios directly — they must be converted to fractions or units first.",
      "DON'T ignore the phrase 'at this rate'. It tells you the quantities scale proportionally.",
      "DON'T round halfway through working. Round only once, at the very end.",
      "DON'T confuse '30% more' with '30% of'. Sketch it on the side if unsure.",
      "DON'T assume the unknown in a rate question is always the final answer — sometimes you need to solve for an intermediate rate first.",
      "DON'T spend 8 minutes on a single ratio question — bank the method mark with a labelled model and move on.",
    ],
    closing:
      "Ratio, percentage, rate — see the parts, fix the units, and the answer is usually one clean step away.",
  },

  // --- 2025-10 ---
  {
    slug: "psle-math-fractions-decimals-dos-donts",
    subject: "Math",
    publishedAt: "2025-10-11",
    title: "PSLE Math Fractions & Decimals: Do's & Don'ts",
    summary:
      "Mastering fractions and decimals gives you back marks across every paper — because almost every question hides one.",
    readTime: "5 min read",
    hero: "/illustrations/feature-feedback.png",
    intro:
      "Weak fraction / decimal handling quietly drains marks across word sums, measurement, geometry, and graphs. Strong handling feels like a small superpower — you solve questions faster and with fewer slips.",
    dos: [
      "DO memorise common fraction ↔ decimal ↔ percentage conversions: 1/4 = 0.25 = 25%, 1/5 = 0.2, 1/8 = 0.125, 3/8 = 0.375, 2/3 ≈ 0.667.",
      "DO find a common denominator before adding or subtracting mixed numbers — always.",
      "DO simplify fractions at every step so the numbers stay small and friendly.",
      "DO convert decimals to fractions (or vice versa) whichever makes the calculation shorter.",
      "DO line up decimal points carefully when writing long multiplication / division by hand.",
      "DO estimate before solving: '1/3 of 450' should be around 150 — if you get 45 or 1350, stop and recheck.",
    ],
    donts: [
      "DON'T multiply or divide mixed numbers directly — convert to improper fractions first.",
      "DON'T 'cancel' diagonally without writing working. It causes silly errors under pressure.",
      "DON'T drop trailing zeros on money or measurement answers — $3.5 is wrong, $3.50 is correct.",
      "DON'T confuse '1/2 more' with '1/2 of'. Re-read the question when unsure.",
      "DON'T forget to convert the final fraction back to a mixed number if the question asks for one.",
      "DON'T write '0.50 = 1/2 = 50%' as separate answers on the line — pick the form the question asks for.",
    ],
    closing:
      "Fractions and decimals are the grammar of PSLE Math. Get them right and everything else reads cleaner.",
  },

  // --- 2025-09 ---
  {
    slug: "psle-english-editing-dos-donts",
    subject: "English",
    publishedAt: "2025-09-13",
    title: "PSLE English Editing: Do's & Don'ts",
    summary:
      "The spelling + grammar editing section is a small-mark question that quietly costs 5–6 marks a year for students who rush it.",
    readTime: "4 min read",
    hero: "/illustrations/hero-class.png",
    intro:
      "Editing is the most predictable section on Paper 2 — the same error types come up every year. Treat it like a checklist, not a guessing game, and it becomes almost-automatic marks.",
    dos: [
      "DO read the whole passage once before correcting anything. Context changes what looks like an error.",
      "DO check for the big six: tense, subject-verb agreement, plurals, articles (a/an/the), prepositions, and word-form errors.",
      "DO circle every underlined word, read the sentence aloud in your head, and check it 'sounds right' grammatically.",
      "DO watch for contextual spelling errors: 'their/there/they're', 'its/it's', 'affect/effect', 'than/then'.",
      "DO write the correction clearly above the error in block letters so the marker can read it.",
      "DO budget 5 minutes maximum — it's a quick-points section, don't let it eat into comprehension time.",
    ],
    donts: [
      "DON'T invent errors where there are none. If a line says 'no error' on your mental scan, leave it — but re-check once.",
      "DON'T just change tense endings without checking consistency across the paragraph.",
      "DON'T over-correct — changing 'walked' to 'walking' just to have an answer is worse than leaving it.",
      "DON'T ignore capitalisation and punctuation inside editing items. Sometimes THAT is the error.",
      "DON'T use correction fluid / multiple crossings. One clear crossed-out word with the correction above.",
      "DON'T rush the last 2–3 items. The final items are usually the trickiest.",
    ],
    closing:
      "Editing rewards calm systematic reading. Slow hands here, fast hands in composition.",
  },

  // --- 2025-08 ---
  {
    slug: "psle-english-grammar-mcq-dos-donts",
    subject: "English",
    publishedAt: "2025-08-09",
    title: "PSLE English Grammar MCQ: Do's & Don'ts",
    summary:
      "A section that's almost 'free marks' for the well-drilled student, and a silent mark-leak for the rusher.",
    readTime: "4 min read",
    hero: "/illustrations/feature-feedback.png",
    intro:
      "Paper 2 Grammar MCQ tests the same 8–10 patterns every year: tense, concord, articles, prepositions, pronouns, determiners, connectors, and verb forms. Drilling the patterns is worth more than any 'tips' list — but here's how to avoid losing the ones you should get.",
    dos: [
      "DO cover the options and predict the answer first. Then look at the options. If your prediction matches one, it's almost always correct.",
      "DO use the 'echo test' for subject-verb agreement: say 'he was' or 'they were' silently and feel the match.",
      "DO learn the top pronoun traps: 'either of them IS', 'neither you nor I AM', 'each of the students HAS'.",
      "DO eliminate 2 clearly wrong options before deciding between the remaining 2.",
      "DO mark your gut answer immediately — changing answers on MCQ grammar is usually a mistake.",
      "DO treat the connectors (although, despite, whereas, however) as different parts of speech — that's where 70% of connector questions hide.",
    ],
    donts: [
      "DON'T spend more than 45 seconds per question. If stuck, mark your best guess and move on.",
      "DON'T be tricked by the longest option. Length doesn't mean correct.",
      "DON'T translate the sentence literally from another language in your head — English grammar follows its own logic.",
      "DON'T rely on 'it sounds right' alone for tricky tense questions. Identify the time-marker in the sentence.",
      "DON'T leave any question blank. A guess has a 25% hit rate; blank is 0%.",
      "DON'T cram grammar the night before. Spaced 10-minute drills over a month beat a 3-hour panic session.",
    ],
    closing:
      "Grammar MCQ is the most trainable section on the paper. Ten minutes a day for a month, and the marks follow.",
  },

  // --- 2025-07 ---
  {
    slug: "psle-english-vocabulary-cloze-dos-donts",
    subject: "English",
    publishedAt: "2025-07-12",
    title: "PSLE English Vocabulary Cloze: Do's & Don'ts",
    summary:
      "The cloze passage is less about vocabulary range than about reading the context carefully — here's how the best students see it.",
    readTime: "5 min read",
    hero: "/illustrations/signup-student.png",
    intro:
      "The vocabulary / cloze passage punishes skim-readers and rewards close readers. The answer is almost always already sitting in the sentence before or after the blank — students just don't look.",
    dos: [
      "DO read the ENTIRE passage through once before filling anything in. The passage is one story, not 15 separate sentences.",
      "DO use context clues: synonyms, contrast words ('but', 'however'), cause-effect ('because', 'so'), and examples.",
      "DO match the part of speech — noun, verb, adjective, adverb — before choosing a word.",
      "DO re-read the sentence with your answer plugged in. It should sound natural and complete.",
      "DO watch for collocations ('heavy rain', 'strong wind', 'take a decision' vs 'make a decision').",
      "DO come back to any skipped blanks after finishing the passage — later sentences often reveal the earlier answer.",
    ],
    donts: [
      "DON'T write the first word that comes to mind. Confirm it fits grammatically AND in meaning.",
      "DON'T mix tenses from the rest of the passage.",
      "DON'T leave a blank — even a reasonable guess has a chance of picking up the mark.",
      "DON'T repeat the same exact word in the blank that's already in the sentence unless it clearly fits.",
      "DON'T overlook small grammar words (articles, prepositions) — they're often the blank.",
      "DON'T panic at an unfamiliar topic. The clues are always in the passage, not in your prior knowledge.",
    ],
    closing:
      "Read the whole story first. Answer from context, not from vocabulary guesswork.",
  },

  // --- 2025-06 : EXISTING ---
  {
    slug: "psle-math-exam-strategy-dos-donts",
    subject: "Math",
    publishedAt: "2025-06-14",
    title: "PSLE Math Exam Strategy: Do's & Don'ts",
    summary:
      "Time, order of attempt, calculator use, and checking routines that protect your marks under exam pressure.",
    readTime: "5 min read",
    hero: "/illustrations/hero-class.png",
    intro:
      "Paper 1 is 1 hour (no calculator) and Paper 2 is 1 hour 30 min (calculator allowed). Most children who under-perform don't run out of maths knowledge — they run out of time or rush checking. Here's the strategy we drill with our P6 students.",
    dos: [
      "DO spend 15 seconds at the start scanning the paper. Decide whether to attempt in order or jump to easier questions first.",
      "DO give Paper 1 MCQs no more than 45 seconds each. If you're stuck, mark it, move on.",
      "DO leave 10 minutes at the end of each paper purely for checking. Not for attempting new questions.",
      "DO re-key tricky Paper 2 calculations into the calculator a second time to double-check.",
      "DO check units, signs, and decimal points on every answer before the bell.",
      "DO sleep properly the week before — tired brains make arithmetic slips worth 2–3 grade drops.",
    ],
    donts: [
      "DON'T spend 10 minutes on a 2-mark MCQ. The opportunity cost is huge — that's three 3-mark short answers you could have secured.",
      "DON'T trust the calculator blindly. Mis-typed brackets or wrong division order happen often.",
      "DON'T change answers in the last minute without a clear reason. First instincts, once carefully calculated, are usually right.",
      "DON'T skip the checking pass. Most students find 3–5 marks of careless errors every paper.",
      "DON'T panic when one question is unfamiliar — the rest of the paper is still winnable.",
      "DON'T cram new topics the night before. Revise familiar problem types and rest.",
    ],
    closing:
      "Exam strategy is practiced, not improvised. Do at least 3 full-paper timed runs in the month before PSLE.",
  },

  // --- 2025-05 ---
  {
    slug: "psle-math-problem-sums-dos-donts",
    subject: "Math",
    publishedAt: "2025-05-10",
    title: "PSLE Math Problem Sums: Do's & Don'ts",
    summary:
      "The model-drawing discipline, working clarity, and unit-checking habits that earn method marks even when the final answer slips.",
    readTime: "6 min read",
    hero: "/illustrations/signup-student.png",
    intro:
      "Problem sums are where PSLE Math is won or lost. The marker awards marks for your working, not just your answer — which means a child with a clear, labelled working can score 3 out of 4 on a sum they got slightly wrong. Here is the discipline.",
    dos: [
      "DO read the question twice before drawing anything. Underline what is given, circle what is being asked.",
      "DO draw a model / bar diagram for every 'before-and-after', 'fraction', 'ratio' or 'remainder' question — it's faster than algebra at P6 and earns method marks.",
      "DO label every bar, variable, and unit clearly. 'Ali had $80' > '80'.",
      "DO write 'Ans:' followed by the full statement with units: 'Ans: Ali has $45 left.'",
      "DO check: does my answer make sense? If a ratio question gives 'John = −3 oranges', you've gone wrong.",
      "DO practice spotting the question type in the first 10 seconds: 'this is a remainder question' → standard approach kicks in.",
    ],
    donts: [
      "DON'T skip working to save time. 0 working = 0 method marks if the final answer is wrong.",
      "DON'T erase incorrect working fully — cross it out neatly with a single line. Sometimes the marker still awards partial marks.",
      "DON'T ignore units. '$' vs 'cents', 'cm' vs 'm', 'kg' vs 'g' — mis-units cost accuracy marks in almost every paper.",
      "DON'T assume diagrams are to scale. If the question gives measurements, use the numbers, not your eye.",
      "DON'T stay stuck on one question for 10+ minutes. Mark it, move on, come back after finishing the rest.",
      "DON'T leave blanks. Even a labelled model with one attempted line of working can pick up 1 method mark.",
    ],
    closing:
      "At The Tutor's Table we insist on the same 3-line habit for every problem sum: label → model → answer statement. Marks follow method.",
  },

  // --- 2025-04 ---
  {
    slug: "psle-english-comprehension-oral-dos-donts",
    subject: "English",
    publishedAt: "2025-04-12",
    title: "PSLE English Comprehension & Oral: Do's & Don'ts",
    summary:
      "How to answer Paper 2 open-ended comprehension with confidence, and how to speak well in Stimulus-Based Conversation.",
    readTime: "5 min read",
    hero: "/illustrations/hero-class.png",
    intro:
      "Paper 2 rewards careful reading more than it rewards 'bombastic' vocabulary. Oral rewards natural, thoughtful answers more than memorised phrases. Here is what works — and what costs marks every year.",
    dos: [
      "DO underline the key word in every question — 'why', 'how', 'what', 'two reasons'. The question dictates the answer shape.",
      "DO answer in full sentences that 'borrow' words from the question to anchor your answer.",
      "DO use evidence from the passage, quoted exactly where possible, for inference questions.",
      "DO practice explaining your answer for Oral in 3 sentences: Point → Example / personal experience → Link back to the question.",
      "DO make eye contact and smile during Oral. Examiners mark on 'ease of communication' too.",
      "DO ask for clarification if you truly don't understand an Oral question — it's better than freezing.",
    ],
    donts: [
      "DON'T copy a whole chunk from the passage as your answer — markers penalise lifting without processing.",
      "DON'T answer 'why' questions with just a 'because'. Always unpack the reason.",
      "DON'T leave vocabulary-in-context questions blank. Look at the sentence before and after for clues.",
      "DON'T speak in one-word answers during Oral. Even 'Yes' needs 2–3 sentences of reasoning.",
      "DON'T memorise generic Oral responses — examiners hear them and mark down for unnatural delivery.",
      "DON'T rush. Taking a breath before answering is a sign of confidence, not weakness.",
    ],
    closing:
      "Daily 10-minute reading of a newspaper article + summarising it in 3 sentences is the single most effective prep for both comprehension and oral.",
  },

  // --- 2025-03 ---
  {
    slug: "psle-english-composition-dos-donts",
    subject: "English",
    publishedAt: "2025-03-15",
    title: "PSLE English Composition: Do's & Don'ts",
    summary:
      "A clear, practical checklist for writing a Paper 1 composition that scores — from planning to the last sentence.",
    readTime: "6 min read",
    hero: "/illustrations/feature-feedback.png",
    intro:
      "Paper 1 Composition is worth 40 marks and marked on Content (20), Language & Organisation (20). A good PSLE composition is not a long composition — it is a controlled, clearly-paragraphed, sharply-chosen one. Use this checklist before, during, and after you write.",
    dos: [
      "DO spend the first 5 minutes planning. Jot down a simple 4-paragraph arc: Setting → Rising action → Climax → Resolution with a reflection.",
      "DO pick the picture that gives you the most mileage, not the one that 'looks easiest'. Familiar vocabulary beats ambitious vocabulary every time.",
      "DO vary sentence openings. Mix 'I …' starters with participle openings ('Heart pounding, I …'), adverb openings, and short punchy sentences.",
      "DO show emotions through action and sensory detail. Instead of 'I was scared', try 'My knees turned to jelly and I could hear every heartbeat.'",
      "DO build to one clear climax. Every paragraph should move the story forward.",
      "DO leave 3–5 minutes at the end to proofread for tense slips, plurals, capitalisation, and spelling. These are the cheapest marks in the entire paper.",
    ],
    donts: [
      "DON'T write a 5-page essay. Examiners reward quality, not quantity. 300–400 words with strong control > 600 rambling words.",
      "DON'T memorise a full composition and force-fit it to the theme. It almost always shows, and off-topic pieces are penalised heavily.",
      "DON'T overuse 'bombastic' words you don't fully understand. A misused ambitious word loses marks; a simple accurate word earns them.",
      "DON'T switch between past and present tense mid-paragraph. Decide your tense on the plan line and stick to it.",
      "DON'T end abruptly or with 'And then I woke up.' Land the story with a short, reflective final line.",
      "DON'T ignore the pictures and the theme. Your composition must clearly connect to at least one picture.",
    ],
    closing:
      "One habit beats everything else: read your own work out loud once after writing. If a sentence sounds odd to your ear, it is odd on paper too.",
  },
];

// Sort newest first by publishedAt
export const ARTICLES = [...RAW_ARTICLES].sort((a, b) =>
  b.publishedAt.localeCompare(a.publishedAt)
);

export const getArticleBySlug = (slug) =>
  ARTICLES.find((a) => a.slug === slug);

export const formatPublishDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
